import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  UserPlus,
  User,
  Shield,
  Mail,
  Key,
  CheckCircle,
  Copy,
  Eye,
  EyeOff,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PersonnelPromoteToUser({ personnelId, person, onUpdate }) {
  const [userAccount, setUserAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [sendInvitation, setSendInvitation] = useState(true);
  const [tempPassword, setTempPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [permissionLevels, setPermissionLevels] = useState([]);

  useEffect(() => {
    fetchUserAccount();
    fetchPermissionLevels();
  }, [personnelId]);

  const fetchUserAccount = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/personnel/${personnelId}/user-account`);
      if (response.data.has_user_account) {
        setUserAccount(response.data.user);
      }
    } catch (error) {
      console.error('Failed to fetch user account:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionLevels = async () => {
    try {
      const response = await axios.get(`${API}/permission-levels`);
      setPermissionLevels(response.data || []);
    } catch (error) {
      console.error('Failed to fetch permission levels:', error);
    }
  };

  const handlePromote = async () => {
    const email = person?.email || person?.work_email;
    if (!email) {
      toast.error('Personnel must have an email address to create a user account');
      return;
    }

    setPromoting(true);
    try {
      const response = await axios.post(`${API}/personnel/${personnelId}/promote-to-user`, {
        role: selectedRole,
        send_invitation: sendInvitation
      });

      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.temp_password) {
          setTempPassword(response.data.temp_password);
        }
        fetchUserAccount();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user account');
    } finally {
      setPromoting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return <div className="p-4 text-center text-muted-foreground">Loading...</div>;
  }

  // If already has user account
  if (userAccount) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle className="text-green-600" size={20} />
          <h3 className="font-semibold">User Account Active</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <User size={18} className="text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{userAccount.email}</p>
              <p className="text-xs text-muted-foreground">Email</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Shield size={18} className="text-muted-foreground" />
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium capitalize">{userAccount.role}</p>
                <Badge className={userAccount.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                  {userAccount.role === 'admin' ? 'Administrator' : 'Viewer'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">Role</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Key size={18} className="text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{userAccount.is_active ? 'Active' : 'Inactive'}</p>
              <p className="text-xs text-muted-foreground">Account Status</p>
            </div>
            <Badge className={userAccount.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
              {userAccount.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          {userAccount.must_change_password && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                User must change password on next login
              </p>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // No user account - show promote form
  const email = person?.email || person?.work_email;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus size={20} />
        <h3 className="font-semibold">Create User Account</h3>
      </div>

      {!email ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            This personnel record does not have an email address. Please add an email address before creating a user account.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Create a user account for <strong>{person?.first_name} {person?.last_name}</strong> to allow them to log into the system.
          </p>

          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-muted-foreground" />
              <span className="text-sm">{email}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">This email will be used for login</p>
          </div>

          <div>
            <label className="text-sm font-medium">Account Role</label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button
                type="button"
                className={`p-3 border rounded-lg text-left transition-colors ${
                  selectedRole === 'viewer' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setSelectedRole('viewer')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <User size={16} />
                  <span className="font-medium">Viewer</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Can view data and their own profile. Limited access.
                </p>
              </button>

              <button
                type="button"
                className={`p-3 border rounded-lg text-left transition-colors ${
                  selectedRole === 'admin' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setSelectedRole('admin')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Shield size={16} />
                  <span className="font-medium">Administrator</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Full access to manage all data and settings.
                </p>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendInvitation"
              checked={sendInvitation}
              onChange={(e) => setSendInvitation(e.target.checked)}
              className="rounded border-input"
            />
            <label htmlFor="sendInvitation" className="text-sm">
              Send invitation email with login instructions
            </label>
          </div>

          <Button
            onClick={handlePromote}
            disabled={promoting}
            className="w-full"
            data-testid="promote-to-user-btn"
          >
            {promoting ? (
              'Creating Account...'
            ) : (
              <>
                <UserPlus size={16} className="mr-2" />
                Create {selectedRole === 'admin' ? 'Admin' : 'Viewer'} Account
              </>
            )}
          </Button>

          {/* Show temp password if invitation wasn't sent */}
          {tempPassword && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-800 mb-2">
                Account created successfully!
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm">Temporary Password:</span>
                <code className="px-2 py-1 bg-white rounded text-sm">
                  {showPassword ? tempPassword : '••••••••••••'}
                </code>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1 hover:bg-green-100 rounded"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  onClick={() => copyToClipboard(tempPassword)}
                  className="p-1 hover:bg-green-100 rounded"
                >
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-xs text-green-700 mt-2">
                Share this password securely. User will be required to change it on first login.
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
